import { atom } from 'jotai';
import { atomWithDefault, atomWithLazy, loadable } from 'jotai/utils';
import { FieldEntity, IField } from '../../../data-models/field2.data-model';
import { ITemplatesResponse } from '../../../data-models/template.data-model';
import { isCustomField } from '../../../util/field-utils';
import { readThroughAtom } from '../../boot-cache/bootCache';
import { fetchCustomFields, fetchCustomFieldTemplates } from '../../queries/MaggieCustomFieldQueries';
import { FieldDefinition } from '../../../data-models/field-definition/field-definition.schema';
import { customFieldToFieldDefinition } from '../../../data-models/field-definition/custom-field-mapping';

type TemplatesTypeValue = ITemplatesResponse | null | Promise<ITemplatesResponse | null>;

/**
 * Default fetches the available custom-field templates on first subscribe.
 * Consumers using `useAtomValue` will suspend on first read; use
 * {@link templatesTypeListStateLoadableJ} for non-suspending callers.
 */
export const templatesTypeListStateJ = atomWithDefault<TemplatesTypeValue>(async () => {
  return (await fetchCustomFieldTemplates()) ?? null;
});

export const templatesTypeListStateLoadableJ = loadable(templatesTypeListStateJ);

export const customFieldsSelectedTemplateState = atom<string>('company');

export const customFieldsOpenUploadModalState = atom<boolean>(false);

type CustomFieldsValue = IField<unknown>[] | Promise<IField<unknown>[]>;

export const customFieldsListAtom = atomWithLazy<CustomFieldsValue>((): Promise<IField<unknown>[]> =>
  readThroughAtom(customFieldsListAtom, 'customFields', () => fetchCustomFields<unknown>())
);
export const customFieldsListLoadable = loadable(customFieldsListAtom);

export const customFieldsByIdMapAtom = atom(async (get) => {
  const fields = await get(customFieldsListAtom);
  if (!fields) {
    return new Map<number, IField<unknown>>();
  }
  return new Map(fields.map((field) => [field.id, field]));
});
export const customFieldsByIdMapLoadable = loadable(customFieldsByIdMapAtom);

export const customFieldsByEntityAtom = atom(async (get) => {
  const fields = (await get(customFieldsListAtom)) ?? [];

  return fields.reduce((res, field) => {
    const entity = field.entity as FieldEntity;
    if (isCustomField(field)) {
      if (res.has(entity)) {
        res.get(entity)!.push(field);
      } else {
        res.set(entity, [field]);
      }
    }

    return res;
  }, new Map<FieldEntity, IField<unknown>[]>());
});

export const customFieldsByEntityLoadable = loadable(customFieldsByEntityAtom);

export function isCustomFieldKey(key: string) {
  return key.startsWith('customData.');
}

/**
 * Canonical custom-field definitions, derived from `customFieldsListAtom`
 * (single network call). Filtered to the actual custom-field rows —
 * the underlying list contains both KPI and custom fields. Per the
 * FD-2794 cross-cutting rule, the legacy atom is the source of truth
 * during migration; this atom only re-projects.
 */
export const customFieldDefinitionAtom = atom(async (get): Promise<FieldDefinition[]> => {
  const fields = (await get(customFieldsListAtom)) ?? [];
  return fields.filter(isCustomField).map(customFieldToFieldDefinition);
});

export const customFieldDefinitionByKeyMapAtom = atom(async (get) => {
  const defs = await get(customFieldDefinitionAtom);
  return new Map(defs.map((d) => [d.key, d]));
});

export const customFieldDefinitionLoadable = loadable(customFieldDefinitionAtom);
